import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HelpIcon from "@mui/icons-material/Help";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { PowerBIButton } from "src/reusable/components/PowerBIComponents";
import { Cap2RecordDetail } from "./Cap2RecordDetail";
import {
  SOURCE_ID_MAX_CHARS,
  SourceIdText,
  sourceIdTextWidthPx,
} from "../shared/sourceIdColumn";
import {
  downloadShadowComparisonExport,
  shadowComparisonExportUrl,
  useShadowComparisonRecords,
} from "./ShadowBetaServices";

const ROWS_PER_PAGE = 50;

// Row tinting mirrors the production audit table: green for screened rows
// with no known miss, amber when marked inaccurate, white when CAP 2.0 has
// not screened the record.
const rowBackground = (row) => {
  if (row.cap2_record_id == null) {
    return "transparent";
  }
  const accurate = row.critical_accurate ?? row.noncritical_accurate;
  if (accurate === false) {
    return "#fff8e1";
  }
  return "#edf7f0";
};

const headerCellSx = {
  fontSize: 12,
  fontWeight: 600,
  color: "text.secondary",
  whiteSpace: "nowrap",
  borderBottom: "2px solid",
  borderColor: "divider",
};

const bodyCellSx = { fontSize: 13, py: 0.75 };

// CAP 2.0 accent, shared with the accuracy header's chart series.
const CAP2_COLOR = "#6366f1";

// iOS-style pill switch matching the mockup toolbar: filled indigo track
// with a white thumb when on, gray when off.
const PillSwitch = styled(Switch)(({ theme }) => ({
  width: 46,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 3,
    "&.Mui-checked": {
      transform: "translateX(20px)",
      color: "#fff",
      "& + .MuiSwitch-track": { backgroundColor: CAP2_COLOR, opacity: 1 },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: theme.palette.grey[300],
    opacity: 1,
  },
}));

/**
 * Label-over-switch-over-caption filter, matching the v1 toolbar's
 * semantics: off shows every record ("ALL"), on narrows to the labeled
 * subset ("only").
 */
const FilterSwitch = ({ label, checked, onChange, testId }) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
    <PillSwitch
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      inputProps={{ "data-testid": testId }}
      sx={{ my: 0.5 }}
    />
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", lineHeight: 1 }}
    >
      {checked ? "only" : "ALL"}
    </Typography>
  </Box>
);

FilterSwitch.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
};

const AccurateCell = ({ row }) => {
  if (row.cap2_record_id == null) {
    const color = { failed: "error", aborted: "default" }[row.status] || "info";
    const chip = (
      <Chip
        size="small"
        variant="outlined"
        color={color}
        label={row.status}
        data-testid={`cap2-status-${row.source_uid}`}
      />
    );
    return row.last_error ? (
      <Tooltip title={row.last_error}>{chip}</Tooltip>
    ) : (
      chip
    );
  }
  const accurate = row.critical_accurate ?? row.noncritical_accurate;
  if (accurate === true) {
    return <CheckCircleIcon color="success" fontSize="small" />;
  }
  if (accurate === false) {
    return <WarningAmberIcon color="warning" fontSize="small" />;
  }
  return <HelpIcon color="info" fontSize="small" />;
};

AccurateCell.propTypes = {
  row: PropTypes.object.isRequired,
};

const PredictionCell = ({ row, outputName }) => {
  const prediction = row.cap2_predictions?.[outputName];
  const divergence = row.divergence_detail?.[outputName];
  if (!prediction) {
    return null;
  }
  return (
    <span data-testid={`cap2-cell-${row.source_uid}-${outputName}`}>
      {prediction.labels.join("; ")}
      {divergence && (
        <Tooltip
          title={`CAP 1.0 said ${divergence.cap1.join("; ") || "(none)"}`}
        >
          <span
            data-testid={`cap2-divergence-dot-${row.source_uid}-${outputName}`}
            style={{ color: "#e55353", marginLeft: 4, cursor: "default" }}
          >
            &#9679;
          </span>
        </Tooltip>
      )}
    </span>
  );
};

PredictionCell.propTypes = {
  row: PropTypes.object.isRequired,
  outputName: PropTypes.string.isRequired,
};

/**
 * The beta audit table, aligned with the production audit table:
 * Automated/Manual only-switches, diverges-only switch, create-date sort,
 * Power BI + CSV export, tinted rows, and dynamic predicted-field columns
 * with divergence dots.
 */
export const Cap2AuditTable = ({
  templateId,
  fields,
  groundTruthDatasetId,
}) => {
  const [divergesOnly, setDivergesOnly] = useState(false);
  const [automated, setAutomated] = useState(null);
  const [manualSample, setManualSample] = useState(null);
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const { data, isLoading } = useShadowComparisonRecords(templateId, {
    divergesOnly,
    automated,
    manualSample,
    order,
    limit: ROWS_PER_PAGE,
    offset: page * ROWS_PER_PAGE,
  });

  const columns = fields?.length
    ? fields.map((f) => ({ key: f.model_output_name, label: f.name }))
    : [
        ...new Set(
          (data?.rows || []).flatMap((row) =>
            Object.keys(row.cap2_predictions || {}),
          ),
        ),
      ].map((key) => ({ key, label: key }));
  const sourceIdWidth = sourceIdTextWidthPx(data?.rows);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <CircularProgress size={24} data-testid="cap2-table-loading" />
      </Box>
    );
  }

  const setFilter = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  return (
    <Box data-testid="cap2-audit-table">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <FilterSwitch
          label="Automated"
          checked={automated === true}
          onChange={(checked) => setFilter(setAutomated)(checked ? true : null)}
          testId="cap2-filter-automated"
        />
        <FilterSwitch
          label="Manual"
          checked={manualSample === true}
          onChange={(checked) =>
            setFilter(setManualSample)(checked ? true : null)
          }
          testId="cap2-filter-manual"
        />
        <FilterSwitch
          label="Diverges vs 1.0"
          checked={divergesOnly}
          onChange={setFilter(setDivergesOnly)}
          testId="cap2-diverges-only-toggle"
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 999,
            pl: 2.5,
            pr: 1,
            py: 0.75,
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Sort By
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            data-testid="cap2-sort-field"
          >
            Create Date
          </Typography>
          <Tooltip title={order === "desc" ? "Newest first" : "Oldest first"}>
            <IconButton
              size="small"
              onClick={() =>
                setFilter(setOrder)(order === "desc" ? "asc" : "desc")
              }
              data-testid="cap2-sort-direction"
            >
              {order === "desc" ? (
                <ArrowDropDownIcon fontSize="small" />
              ) : (
                <ArrowDropUpIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <PowerBIButton
          url={shadowComparisonExportUrl(templateId)}
          forPage="automationRecords"
        />
        <Button
          variant="contained"
          color="success"
          disableElevation
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={() =>
            downloadShadowComparisonExport(templateId, divergesOnly)
          }
          data-testid="cap2-download-csv"
        >
          Download CSV
        </Button>
      </Box>
      <TableContainer
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 34 }} />
              <TableCell sx={headerCellSx}>Source ID</TableCell>
              <TableCell sx={headerCellSx}>Create Date</TableCell>
              <TableCell sx={headerCellSx}>Automated</TableCell>
              <TableCell sx={headerCellSx}>Manual Sample</TableCell>
              <TableCell sx={headerCellSx}>Accurate</TableCell>
              {columns.map((column) => (
                <TableCell key={column.key} sx={headerCellSx}>
                  Predicted {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.rows || []).map((row) => (
              <Cap2Row
                key={row.source_uid}
                row={row}
                columns={columns}
                sourceIdWidth={sourceIdWidth}
                fields={fields}
                templateId={templateId}
                groundTruthDatasetId={groundTruthDatasetId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data && data.total === 0 && (
        <Typography sx={{ p: 3, textAlign: "center" }} color="text.secondary">
          {divergesOnly || automated !== null || manualSample !== null
            ? "No records match the current filters."
            : "No CAP 2.0 screenings yet - run a backfill or screen new records."}
        </Typography>
      )}
      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={ROWS_PER_PAGE}
        rowsPerPageOptions={[ROWS_PER_PAGE]}
      />
    </Box>
  );
};

const Cap2Row = ({
  row,
  columns,
  sourceIdWidth,
  fields,
  templateId,
  groundTruthDatasetId,
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow
        hover
        data-testid={`cap2-row-${row.source_uid}`}
        sx={{ backgroundColor: rowBackground(row) }}
      >
        <TableCell sx={{ ...bodyCellSx, p: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded((current) => !current)}
            data-testid={`cap2-expand-${row.source_uid}`}
          >
            {expanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
          {/* CSS leaves min-width on a table cell undefined, so it sits on an inner box. */}
          <Box
            sx={{
              minWidth: sourceIdWidth,
              // Bold text can exceed the per-character allowance, so IDs up to the cap never wrap.
              whiteSpace:
                (row.source_uid?.length ?? 0) <= SOURCE_ID_MAX_CHARS
                  ? "nowrap"
                  : "normal",
            }}
          >
            <SourceIdText
              sourceUid={row.source_uid}
              testId={`cap2-source-id-${row.source_uid}`}
            />
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          {row.create_date ? new Date(row.create_date).toLocaleString() : null}
        </TableCell>
        <TableCell sx={bodyCellSx}>
          {row.automated ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : null}
        </TableCell>
        <TableCell sx={bodyCellSx}>
          {row.manual_sample ? (
            <Tooltip title="Manual sample">
              <HowToRegIcon color="info" fontSize="small" />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <AccurateCell row={row} />
        </TableCell>
        {columns.map((column) => (
          <TableCell key={column.key} sx={bodyCellSx}>
            <PredictionCell row={row} outputName={column.key} />
          </TableCell>
        ))}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6 + columns.length} sx={{ p: 0 }}>
            <Cap2RecordDetail
              row={row}
              templateId={templateId}
              groundTruthDatasetId={groundTruthDatasetId}
              fields={
                fields?.length
                  ? fields
                  : columns.map((column) => ({
                      model_output_name: column.key,
                      name: column.label,
                    }))
              }
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

Cap2Row.propTypes = {
  groundTruthDatasetId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  row: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  sourceIdWidth: PropTypes.number.isRequired,
  fields: PropTypes.array,
};

Cap2AuditTable.propTypes = {
  groundTruthDatasetId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      model_output_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

export default Cap2AuditTable;
